import { useCallback, useEffect, useMemo } from 'react';
import { getChildFolders, getFolderPath } from '../../../../utils/docOrganization';
import { isAdminOnlyDoc, isMissionNotesDoc } from '../../../../utils/docTags';
import { compareItems, docTypeLabel } from '../utils/browseUtils';

export function useBrowseItems({
  organization,
  docs,
  folderId,
  canViewAdminDocs,
  showMissionNotes,
  sort,
  dir,
  onNavigateFolder,
}) {
  const folders = organization?.folders || [];

  useEffect(() => {
    if (!organization?.ready || !folderId) return;
    if (!folders.some((f) => f.id === folderId)) {
      onNavigateFolder?.(null);
    }
  }, [organization?.ready, folderId, folders, onNavigateFolder]);

  const path = useMemo(
    () => (folderId ? getFolderPath(folders, folderId) : []),
    [folders, folderId]
  );

  const childFolders = useMemo(
    () => getChildFolders(folders, folderId),
    [folders, folderId]
  );

  const docsHere = useMemo(() => {
    const map = organization?.docFolders || {};
    return docs.filter((d) => {
      if (!canViewAdminDocs && isAdminOnlyDoc(d)) return false;
      if (!showMissionNotes && isMissionNotesDoc(d)) return false;
      const fid = map[d.id] || null;
      return fid === (folderId || null);
    });
  }, [docs, organization?.docFolders, folderId, canViewAdminDocs, showMissionNotes]);

  const docCountIn = useCallback(
    (fid) => {
      const map = organization?.docFolders || {};
      return Object.values(map).filter((id) => id === fid).length;
    },
    [organization?.docFolders]
  );

  const latestDocDateIn = useCallback(
    (fid) => {
      const map = organization?.docFolders || {};
      let latest = '';
      for (const d of docs) {
        if (map[d.id] !== fid) continue;
        const at = d.updatedAt || d.createdAt || '';
        if (at > latest) latest = at;
      }
      return latest;
    },
    [docs, organization?.docFolders]
  );

  const folderItems = useMemo(() => {
    const list = childFolders.map((f) => {
      const kids = getChildFolders(folders, f.id).length;
      const docsN = docCountIn(f.id);
      return {
        kind: 'folder',
        id: f.id,
        name: f.name || 'Untitled folder',
        date: latestDocDateIn(f.id),
        type: 'Folder',
        count: docsN,
        meta: `${docsN} doc${docsN === 1 ? '' : 's'}${
          kids ? ` · ${kids} folder${kids === 1 ? '' : 's'}` : ''
        }`,
      };
    });
    return list.sort((a, b) => compareItems(a, b, sort, dir));
  }, [childFolders, folders, docCountIn, latestDocDateIn, sort, dir]);

  const docItems = useMemo(() => {
    const list = docsHere.map((d) => ({
      kind: 'doc',
      id: d.id,
      name: d.title || 'Untitled',
      date: d.updatedAt || d.createdAt || '',
      type: docTypeLabel(d),
      count: null,
      doc: d,
    }));
    return list.sort((a, b) => compareItems(a, b, sort, dir));
  }, [docsHere, sort, dir]);

  return { path, folderItems, docItems };
}
